/**
 * DNS Record Name Utilities
 * Centralized handling of @ symbol and FQDN normalization
 */

/**
 * Normalize DNS record name for API submission
 * Converts @ to empty string, strips domain suffix if present
 *
 * @param name - User input (e.g., "@", "www", "www.example.com")
 * @param domainName - The zone's domain name (e.g., "example.com")
 * @returns Normalized name for API (e.g., "", "www")
 */
export function normalizeNameForApi(name: string, domainName?: string): string {
  const trimmed = name.trim()

  // @ or empty → empty string (apex)
  if (!trimmed || trimmed === '@') {
    return ''
  }

  // Strip trailing dot if present
  const normalized = trimmed.endsWith('.') ? trimmed.slice(0, -1) : trimmed

  // Strip domain suffix if present (case-insensitive)
  if (domainName) {
    const lowerName = normalized.toLowerCase()
    const lowerDomain = domainName.toLowerCase()

    // Full match → apex
    if (lowerName === lowerDomain) {
      return ''
    }

    // Ends with .domain → strip suffix
    const suffix = `.${lowerDomain}`
    if (lowerName.endsWith(suffix)) {
      return normalized.slice(0, -suffix.length)
    }
  }

  return normalized
}

/**
 * Format DNS record name for display
 * Converts empty string to @, strips domain suffix
 *
 * @param name - API value (e.g., "", "www", "www.example.com")
 * @param domainName - The zone's domain name
 * @returns Display value (e.g., "@", "www")
 */
export function formatNameForDisplay(name: string, domainName?: string): string {
  if (!name || name === '') {
    return '@'
  }

  // Strip domain suffix if present
  if (domainName) {
    const lowerName = name.toLowerCase()
    const lowerDomain = domainName.toLowerCase()

    if (lowerName === lowerDomain) {
      return '@'
    }

    const suffix = `.${lowerDomain}`
    if (lowerName.endsWith(suffix)) {
      const stripped = name.slice(0, -suffix.length)
      return stripped || '@'
    }
  }

  return name
}
